import { HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter } from 'lodash';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { catchError, mergeMap, switchMap, takeLast, tap } from 'rxjs/operators';
import { BackgroundJobNotificationService } from 'Src/ng2/shared/services/background-job-notification-service/background-job-notification-service';
import { IStudentPath } from 'src/ng2/shared/typings/interfaces/studentPaths.interface';
import { ApiService } from '../../shared/services/api-service/api-service';
import * as studentPathsActions from '../actions/student-paths-actions';
import { BackgroundJob } from './../../shared/services/background-job/background-job.service';
import { ISnackBarData, SnackBarService } from './../../shared/services/snackbar/snackbar.service';
import { LoadStudentMilestones } from './../actions/milestone-actions';
import { STUDENT_PATH_PROJECTION } from './projections';

@Injectable()
export class StudentPathsEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private injector: Injector,
    private backgroundJob: BackgroundJob,
    private snackBarService: SnackBarService,
    private store: Store<any>,
    private backgroundJobNotificationService: BackgroundJobNotificationService,
  ) {}

  snackBarData: ISnackBarData = { message: '', duration: 2000 };

  private getJobPromise(response: HttpResponse<any>, msg: string): Promise<any> {
    const jobId = response.headers.get('nv-background-jobs');
    const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
    const promise = (this.backgroundJob as any)(jobId).promise();
    const job = null;
    backgroundJobSpinner.activateSpinner(msg, promise, job);
    return promise;
  }

  @Effect()
  loadAllStudentPaths$ = this.actions$.pipe(
    ofType(studentPathsActions.LOAD_ALL_STUDENT_PATHS),
    switchMap((action: { type: string; payload: any }) => {
      const { schoolId } = action.payload;
      const projection = STUDENT_PATH_PROJECTION;
      const where = { status: { $nin: ['DELETED', 'CLOSED'] } };
      const queries = {
        where,
      };
      const payload = { schoolId, projection, queries };
      return this.apiService.getStudentPaths(payload).pipe(
        switchMap(studentPaths => {
          // temporary fix to remove 'DELETED' and 'CLOSED' studentPaths, remove once backend is updated to apply filter (JE)
          const filteredPaths = filter(studentPaths, (studentPath: IStudentPath) => {
            return studentPath.status !== 'DELETED' && studentPath.status !== 'CLOSED';
          });
          return [new studentPathsActions.LoadAllStudentPathSuccess(filteredPaths)];
        }),
        catchError(error => of(new studentPathsActions.LoadAllStudentPathFail(error))),
      );
    }),
  );

  @Effect()
  loadStudentPaths$ = this.actions$.pipe(
    ofType(studentPathsActions.LOAD_STUDENT_PATH),
    switchMap((action: { type: string; payload: any }) => {
      const { schoolId, studentId } = action.payload;
      const payload = { schoolId, studentId };
      return this.apiService.getStudentPaths(payload).pipe(
        switchMap(paths => {
          return [new studentPathsActions.LoadStudentPathSuccess({ paths, studentId })];
        }),
        catchError(error => of(new studentPathsActions.LoadStudentPathFail(error))),
      );
    }),
  );

  @Effect()
  createStudentPath$ = this.actions$.pipe(
    ofType(studentPathsActions.CREATE_STUDENT_PATH),
    switchMap((action: any) => {
      const { schoolId, studentId, pathId } = action.payload;

      return this.apiService.createStudentPath(schoolId, studentId, pathId).pipe(
        mergeMap(path => {
          return [new studentPathsActions.CreateStudentPathSuccess(path)];
        }),
        catchError(error => {
          return of(new studentPathsActions.CreateStudentPathFail(error));
        }),
      );
    }),
  );
  @Effect({ dispatch: false })
  bulkCreateStudentPaths$ = this.actions$.pipe(
    ofType(studentPathsActions.BULK_CREATE_STUDENT_PATH),
    switchMap((action: any) => {
      const { payload } = action;
      const httpResponseObsv$: Observable<HttpResponse<object>> = this.apiService.bulkCreateStudentPath(payload);
      return httpResponseObsv$.pipe(
        takeLast(1),
        switchMap(response => {
          return of(this.getJobPromise(response, 'Creating postsecondary paths'));
        }),
        tap(promise => {
          promise.then(() => {
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentPathCreate' });
            this.snackBarService.showBatchActionToast({ success: true });
          });
        }),
        catchError(error => {
          this.snackBarService.showBatchActionToast({ success: false });
          return of(new studentPathsActions.BulkCreateStudentPathFail(error));
        }),
      );
    }),
  );

  @Effect()
  updateStudentPath$ = this.actions$.pipe(
    ofType(studentPathsActions.UPDATE_STUDENT_PATH),
    switchMap((action: any) => {
      const { studentPathId, status, planDowngrade, schoolId, studentId } = action.payload;
      // two paths can't have a 'Plans to enroll' status, so the planDowngrade property
      // is used to make sure an old path is downgraded before a new path is upgraded (JE)
      if (planDowngrade) {
        const { pathToDowngrade, newStatus } = planDowngrade;
        const payload = { studentPathId, status };
        return this.apiService.updateStudentPath(pathToDowngrade, newStatus).pipe(
          mergeMap(path => {
            return [
              new studentPathsActions.UpdateStudentPathSuccess(path),
              new studentPathsActions.UpdateStudentPath(payload),
              new LoadStudentMilestones({ schoolId, studentId }),
            ];
          }),
          catchError(error => {
            return of(new studentPathsActions.UpdateStudentPathFail(error));
          }),
        );
      } else {
        return this.apiService.updateStudentPath(studentPathId, status).pipe(
          mergeMap(path => {
            return [
              new studentPathsActions.UpdateStudentPathSuccess(path),
              new LoadStudentMilestones({ schoolId, studentId }),
            ];
          }),
          catchError(error => {
            return of(new studentPathsActions.UpdateStudentPathFail(error));
          }),
        );
      }
    }),
  );

  @Effect({ dispatch: false })
  bulkUpdateStudentPaths$ = this.actions$.pipe(
    ofType(studentPathsActions.BULK_UPDATE_STUDENT_PATH),
    switchMap((action: any) => {
      const { payload } = action;
      const httpResponseObsv$: Observable<HttpResponse<object>> = this.apiService.patchStudentPaths(payload);
      return httpResponseObsv$.pipe(
        takeLast(1),
        switchMap(response => {
          return of(this.getJobPromise(response, 'Updating postsecondary paths'));
        }),
        tap(promise => {
          promise.then(() => {
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentPathUpdate' });
            this.snackBarService.showBatchActionToast({ success: true });
          });
        }),
        catchError(error => {
          this.snackBarService.showBatchActionToast({ success: false });
          return of(new studentPathsActions.BulkUpdateStudentPathFail(error));
        }),
      );
    }),
  );
}
